import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button, Card, Input } from "reactstrap";
import notifications from "../../common/Components/Notifications/Notification";
import HKPConfirmDialog from "../../common/Components/HKPConfirmDialog";
import { duplicateCareTemplate, listCareTemplates, setCareTemplateArchived } from "../../care/careApi";
import { careCopy } from "../../care/careCopy";
import type { CareTemplate } from "../../care/careTypes";
import { filledSectionCount } from "../../care/careValues";
import { canUseCare } from "../../helpers/careEnabled";
import { readAuthUser } from "../../helpers/studioModules";
import CareTemplateEditor from "./CareTemplateEditor";
import "./careTemplates.scss";

type Tab = "mine" | "library";
type Status = "ACTIVE" | "ARCHIVED";

/** Settings → Care Templates: the studio's own templates and the HKP library. */
const CareTemplates = () => {
  const history = useHistory();
  const role = readAuthUser()?.users?.role;
  const superAdmin = role === "SUPER_ADMIN";
  const [tab, setTab] = useState<Tab>(superAdmin ? "library" : "mine");
  const [status, setStatus] = useState<Status>("ACTIVE");
  const [search, setSearch] = useState("");
  const [templates, setTemplates] = useState<CareTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [drawer, setDrawer] = useState<{ open: boolean; template: CareTemplate | null }>({ open: false, template: null });
  const [archiving, setArchiving] = useState<CareTemplate | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const res = await listCareTemplates({ status: "ALL" });
    if (res.success) setTemplates(res.data || []);
    else setError(res.message || "Unable to load care templates.");
    setLoading(false);
  }, []);

  useEffect(() => {
    if (canUseCare(role)) void load();
  }, [load, role]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return templates.filter((t) => {
      if ((tab === "library") !== (t.source === "GLOBAL")) return false;
      if (t.status !== status) return false;
      return !q || t.name.toLowerCase().includes(q);
    });
  }, [templates, tab, status, search]);

  if (!canUseCare(role)) {
    return (
      <div className="userMainDiv hkp-care-templates">
        <Card className="createProductCard p-3">
          <h4 className="mb-2">{careCopy.pageTitle}</h4>
          <p className="text-muted mb-0">Care & Preservation is not switched on for your studio. Ask HKP to enable it.</p>
        </Card>
      </div>
    );
  }

  const duplicate = async (t: CareTemplate) => {
    setBusy(true);
    const res = await duplicateCareTemplate(t.id);
    setBusy(false);
    if (!res.success || !res.data) {
      notifications({ title: "Error!", message: res.message || "Unable to duplicate the template.", type: "danger" });
      return;
    }
    notifications({ title: "Success!", message: `“${res.data.name}” added to your templates`, type: "success" });
    await load();
    setTab(res.data.source === "GLOBAL" ? "library" : "mine");
    setDrawer({ open: true, template: res.data });
  };

  const toggleArchive = async (t: CareTemplate, archive: boolean) => {
    setBusy(true);
    const res = await setCareTemplateArchived(t.id, archive, t.version);
    setBusy(false);
    setArchiving(null);
    if (!res.success) {
      notifications({
        title: "Error!",
        message: res.code === "VERSION_CONFLICT" ? careCopy.conflict : res.message || "Unable to update the template.",
        type: "danger",
      });
      void load();
      return;
    }
    notifications({ title: "Success!", message: archive ? "Template archived" : "Template restored", type: "success" });
    void load();
  };

  const emptyText = status === "ARCHIVED" ? careCopy.emptyArchived : tab === "library" ? careCopy.emptyLibrary : careCopy.emptyMine;
  const canCreateHere = tab === "mine" ? !superAdmin : superAdmin;

  return (
    <div className="userMainDiv hkp-care-templates">
      <Card className="createProductCard p-3">
        <div className="hkp-care-templates__head">
          <div>
            <h4 className="mb-2">{careCopy.pageTitle}</h4>
            <p className="text-muted mb-0">{careCopy.pageSubtitle}</p>
          </div>
          {canCreateHere && (
            <Button color="primary" size="sm" onClick={() => setDrawer({ open: true, template: null })}>
              {careCopy.newTemplate}
            </Button>
          )}
        </div>

        <div className="hkp-care-templates__tabs" role="tablist">
          {(superAdmin ? (["library"] as Tab[]) : (["mine", "library"] as Tab[])).map((key) => (
            <button key={key} type="button" role="tab" aria-selected={tab === key} className={tab === key ? "is-active" : ""} onClick={() => setTab(key)}>
              {key === "mine" ? careCopy.myTemplates : careCopy.library}
            </button>
          ))}
        </div>

        <div className="hkp-care-templates__filters">
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={careCopy.search} aria-label={careCopy.search} />
          <select className="form-select" value={status} onChange={(e) => setStatus(e.target.value as Status)} aria-label="Status">
            <option value="ACTIVE">{careCopy.active}</option>
            <option value="ARCHIVED">{careCopy.archived}</option>
          </select>
        </div>

        {error && (
          <p className="RequiredField" role="alert">
            {error}{" "}
            <button type="button" className="btn btn-link p-0 align-baseline" onClick={() => void load()}>
              {careCopy.reload}
            </button>
          </p>
        )}

        {loading ? (
          <p className="text-muted" role="status">
            Loading care templates…
          </p>
        ) : visible.length === 0 ? (
          <div className="hkp-care-templates__empty">
            <p>{search ? "No templates match your search." : emptyText}</p>
            {tab === "mine" && !search && status === "ACTIVE" && (
              <Button color="primary" size="sm" outline onClick={() => setTab("library")}>
                Browse the HKP library
              </Button>
            )}
          </div>
        ) : (
          <ul className="hkp-care-templates__list">
            {visible.map((t) => (
              <li className="hkp-care-templates__item" key={t.id}>
                <div className="hkp-care-templates__info">
                  <button type="button" className="hkp-care-templates__name" onClick={() => setDrawer({ open: true, template: t })}>
                    {t.name}
                  </button>
                  <p className="text-muted mb-0">
                    <span className={`hkp-care-templates__badge${t.source === "GLOBAL" ? " is-library" : ""}`}>
                      {t.source === "GLOBAL" ? careCopy.libraryBadge : careCopy.studioBadge}
                    </span>{" "}
                    {careCopy.sectionsFilled(filledSectionCount(t.sections))} · {careCopy.usedOn(t.usedOnProducts || 0)}
                  </p>
                </div>
                <div className="hkp-care-templates__actions">
                  <Button size="sm" className="btn-hastkala-outline" onClick={() => setDrawer({ open: true, template: t })}>
                    {t.editable && t.status === "ACTIVE" ? careCopy.edit : careCopy.view}
                  </Button>
                  {t.status === "ACTIVE" && (
                    <Button size="sm" color="primary" outline disabled={busy || filledSectionCount(t.sections) === 0} onClick={() => history.push(`/care-templates/${t.id}/apply`)}>
                      {careCopy.apply}
                    </Button>
                  )}
                  {!superAdmin || t.source === "GLOBAL" ? (
                    <Button size="sm" className="btn-hastkala-outline" disabled={busy} title={careCopy.duplicateHint} onClick={() => void duplicate(t)}>
                      {careCopy.duplicate}
                    </Button>
                  ) : null}
                  {t.editable &&
                    (t.status === "ACTIVE" ? (
                      <Button size="sm" color="danger" outline disabled={busy} onClick={() => setArchiving(t)}>
                        {careCopy.archive}
                      </Button>
                    ) : (
                      <Button size="sm" color="primary" outline disabled={busy} onClick={() => void toggleArchive(t, false)}>
                        {careCopy.restore}
                      </Button>
                    ))}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <CareTemplateEditor
        open={drawer.open}
        template={drawer.template && drawer.template.status === "ARCHIVED" ? { ...drawer.template, editable: false } : drawer.template}
        onClose={() => setDrawer({ open: false, template: null })}
        onReload={async () => {
          const res = await listCareTemplates({ status: "ALL" });
          if (res.success) {
            setTemplates(res.data || []);
            const fresh = (res.data || []).find((t) => t.id === drawer.template?.id) || null;
            setDrawer({ open: true, template: fresh });
          }
        }}
        onSaved={(saved) => {
          notifications({ title: "Success!", message: "Template saved", type: "success" });
          setDrawer({ open: false, template: null });
          void load();
          setTab(saved.source === "GLOBAL" ? "library" : "mine");
        }}
      />
      <HKPConfirmDialog
        open={Boolean(archiving)}
        title={careCopy.archiveTitle}
        description={careCopy.archiveBody}
        confirmLabel={careCopy.archive}
        busy={busy}
        busyLabel="Archiving…"
        onCancel={() => setArchiving(null)}
        onConfirm={() => archiving && void toggleArchive(archiving, true)}
      />
    </div>
  );
};

export default CareTemplates;
